import React, { useCallback, useMemo, useState } from 'react';
import Button from '../Button';
import ReactDOM from 'react-dom';
import Mold from './Mold';
import MoldDetails from './MoldDetails';
import { MoldsPropType } from '../../helpers/prop-types';
import PropTypes from 'prop-types';
import { normalizeString } from '../../helpers/string';
import styles from './SiliconeMoldPicker.module.scss';
import useKeydown from '../../hooks/use-keydown';
import useLocalize from '../../hooks/use-localize';

const SiliconeMoldPicker = ({ hide, molds, setVolume }) => {
  const { localize } = useLocalize();
  useKeydown('Escape', hide);

  const [textFilter, setSetTextFilter] = useState('');
  const [dataFilter, setDataFilter] = useState(new Set(['Silikomart', 'Pavoni', 'Other', 'single', 'multiple']));
  const [selectedMold, setSelectedMold] = useState(null);

  const normalizedTextFilter = useMemo(() => normalizeString(textFilter), [textFilter]);

  const updateTextFilter = useCallback(
    (event) => {
      const {
        target: { value },
      } = event;
      setSetTextFilter(value);
    },
    [setSetTextFilter]
  );

  const toggleDataFilter = useCallback(
    (event) => {
      const {
        target: { id },
      } = event;

      const newDataFilter = new Set(dataFilter);
      if (dataFilter.has(id)) {
        // Remove
        newDataFilter.delete(id);

        if (id === 'single' && !dataFilter.has('multiple')) {
          newDataFilter.add('multiple');
        } else if (id === 'multiple' && !dataFilter.has('single')) {
          newDataFilter.add('single');
        }
      } else {
        // Add
        newDataFilter.add(id);
      }
      setDataFilter(newDataFilter);
    },
    [dataFilter, setDataFilter]
  );

  const filteredMolds = useMemo(
    () =>
      molds.filter((mold) => {
        const { brand, cavityCount, id, normalizedTitle } = mold;
        const isBrandOK = dataFilter.has(brand) || (dataFilter.has('Other') && brand !== 'Silikomart' && brand !== 'Pavoni');
        const isMoldVisible =
          ((cavityCount === 1 && dataFilter.has('single')) || (cavityCount > 1 && dataFilter.has('multiple'))) &&
          isBrandOK &&
          (!normalizedTextFilter || normalizedTitle.indexOf(normalizedTextFilter) > -1);

        if (!isMoldVisible && id === selectedMold?.id) {
          setSelectedMold(null);
        }

        return isMoldVisible;
      }),
    [dataFilter, selectedMold, normalizedTextFilter]
  );

  const handleOnSelect = useCallback(
    (event, mold = selectedMold) => {
      if (!mold) {
        return;
      }

      const { cavityCount, cavityVolume } = mold;
      setVolume(cavityCount * cavityVolume);
      hide();
    },
    [hide, selectedMold, setVolume]
  );

  return ReactDOM.createPortal(
    <React.Fragment>
      <div className={styles.modalOverlay} />
      <div className={styles.modalWrapper} aria-modal aria-hidden tabIndex={-1} role='dialog'>
        <div className={styles.modal}>
          <button type='button' className={styles.modalCloseButton} data-dismiss='modal' aria-label='Close' onClick={hide}>
            <span aria-hidden='true'>&times;</span>
          </button>
          <div className={styles.modalHeader}>
            <div className={styles.modalHeaderFiltersLabel}>
              <div>{localize('mold_picker.filters.label')}</div>
              <div className={styles.moldCount}>{localize('mold_picker.mold_count', { count: filteredMolds.length })}</div>
            </div>
            <div className={styles.modalHeaderFilters}>
              <input onChange={updateTextFilter} placeholder={localize('mold_picker.filters.placeholder')} type='search' value={textFilter} />
              <div className={styles.dataFilters}>
                <input checked={dataFilter.has('Silikomart')} className={styles.labelOnly} id='Silikomart' onChange={toggleDataFilter} type='checkbox' />
                <label htmlFor='Silikomart'>
                  <div>Silikomart</div>
                </label>
                <input checked={dataFilter.has('Pavoni')} className={styles.labelOnly} id='Pavoni' onChange={toggleDataFilter} type='checkbox' />
                <label htmlFor='Pavoni'>
                  <div>Pavoni</div>
                </label>
                {molds.some((mold) => mold.brand !== 'Silikomart' && mold.brand !== 'Pavoni') ? (
                  <>
                    <input checked={dataFilter.has('Other')} className={styles.labelOnly} id='Other' onChange={toggleDataFilter} type='checkbox' />
                    <label htmlFor='Other'>
                      <div>{localize('mold_picker.filters.other_brands')}</div>
                    </label>
                  </>
                ) : null}
                <input checked={dataFilter.has('single')} className={styles.labelOnly} id='single' onChange={toggleDataFilter} type='checkbox' />
                <label htmlFor='single'>
                  <div>{localize('mold_picker.filters.one_cavity')}</div>
                </label>
                <input checked={dataFilter.has('multiple')} className={styles.labelOnly} id='multiple' onChange={toggleDataFilter} type='checkbox' />
                <label htmlFor='multiple'>
                  <div>{localize('mold_picker.filters.multiple_cavities')}</div>
                </label>
              </div>
            </div>
          </div>
          <MoldDetails className={styles.pickerMoldDetailsHeader}>
            <div className={styles.pickerMoldHeader}>{localize('mold_picker.array_header.link')}</div>
            <div className={styles.pickerMoldHeader}>{localize('mold_picker.array_header.cavity_count')}</div>
            <div className={styles.pickerMoldHeader}>{localize('mold_picker.array_header.volume')}</div>
            <div className={styles.pickerMoldHeader}>{localize('mold_picker.array_header.dimensions')}</div>
          </MoldDetails>
          <div className={styles.modalContent}>
            {filteredMolds.map((mold) => (
              <Mold isSelected={mold.id === selectedMold?.id} key={mold.id} mold={mold} onClick={setSelectedMold} onDoubleClick={handleOnSelect} />
            ))}
            {filteredMolds.length === 0 && (
              <div className={styles.missingMold}>
                <div>{localize('mold_picker.no_match')}</div>
                <a
                  href='mailto:robloche@gmail.com?subject=Moule%20manquant&body=Marque%20du%20moule%20%3A%20%0ANom%20du%20moule%20%3A%20%0ADimensions%20%3A%20%0AVolume%20%3A%20%0ALien%20vers%20sa%20page%20%3A%20'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {localize('mold_picker.write_email_button')}
                </a>
              </div>
            )}
          </div>
          <div className={styles.modalFooter}>
            <Button disabled={selectedMold === null} handleOnClick={handleOnSelect}>
              {localize('mold.choose')}
            </Button>
          </div>
        </div>
      </div>
    </React.Fragment>,
    document.body
  );
};

SiliconeMoldPicker.propTypes = {
  hide: PropTypes.func.isRequired,
  molds: MoldsPropType.isRequired,
  setVolume: PropTypes.func.isRequired,
};

export default SiliconeMoldPicker;
